<script lang="ts" setup generic="T">
import '@egjs/vue3-flicking/dist/flicking.css';

import type Flicking from '@egjs/vue3-flicking';
import { ref } from 'vue';

const props = defineProps<{
    title?: string;
    isLoading?: boolean;
    isError?: boolean;
    data: T[] | undefined;
    breakpoints: Breakpoints;
}>();
const flickingInstance = ref<null | Flicking>(null);
const disabled = ref(false);
const { isDesktop } = useDevice();
const resize = useActiveBreakpoint<Breakpoints>(props.breakpoints, 'min-width', 10, isDesktop ? 'alwaysResize' : 'once');

const emit = defineEmits<{ (e: 'onReady', payload: boolean): void }>();
const isMounted = ref(false);

const disableSwipe = ref(false);

const prevSlide = async () => {
    try {
        const instance = flickingInstance.value;
        if (!instance || disableSwipe.value || instance.animating) return;
        await instance.prev();
    }
    catch (e) {
        console.warn('[prevSlide error]', e);
    }
};

const nextSlide = async () => {
    try {
        const instance = flickingInstance.value;
        if (!instance || disableSwipe.value || instance.animating) return;
        await instance.next();
    }
    catch (e) {
        console.warn('[nextSlide error]', e);
    }
};

function onReady() {
    if (!flickingInstance.value) return;

    emit('onReady', true);

    const viewport = flickingInstance.value.viewport.element;
    const camera = flickingInstance.value.camera.element;

    if (!viewport || !camera) return;

    const viewportWidth = viewport.offsetWidth;
    const cameraWidth = camera.scrollWidth;

    if (!isDesktop) {
        if ((props.data?.length ?? 0) <= panelsPerView.value) {
            disableSwipe.value = true;
            flickingInstance.value.disableInput();
        }
        else {
            disableSwipe.value = false;
            flickingInstance.value.enableInput();
        }
    }
    else {
        if (cameraWidth <= viewportWidth + 1) {
            disableSwipe.value = true;
            flickingInstance.value.disableInput();
        }
        else {
            disableSwipe.value = false;
            flickingInstance.value.enableInput();
        }
    }
}

const panelsPerView = computed(() => {
    return resize.activeBreakpoint.value?.panelsPerView ?? 5;
});

onMounted(() => {
    isMounted.value = true;
});

</script>

<template>
  <div class="width-full display-flex flex-column gap-16">
    <div v-if="title"
         class="display-flex align-items-center width-full justify-between"
    >
      <h2 class="display-800-accent">
        {{ title }}
      </h2>

      <Transition name="fade"
                  mode="out-in"
      >
        <div v-if="!isMounted && !data">
          <LoaderDots class="dots-height" />
        </div>
        <div v-else-if="!disabled && data && data?.length && data.length > panelsPerView"
             class="display-flex align-items-center gap-4"
        >
          <Button :color="'bg.mojito'"
                  size="size.small"
          >
            <button aria-label="previous slide"
                    class="display-flex justify-center align-items-center"
                    type="button"
                    @click="prevSlide()"
                    :disabled="disabled"
            >
              <IconMain :size="24"
                        icon="arrow-l-r"
              />
            </button>
          </Button>
          <Button :color="'bg.mojito'"
                  size="size.small"
          >
            <button aria-label="next slide"
                    class="display-flex justify-center align-items-center"
                    type="button"
                    @click="nextSlide()"
                    :disabled="disabled"
            >
              <IconMain :size="24"
                        icon="arrow-r-l"
              />
            </button>
          </Button>
        </div>
      </Transition>
    </div>

    <template v-if="isError">
      <slot name="error"></slot>
    </template>
    <template v-else-if="!isLoading && data && !data.length">
      <slot name="empty"></slot>
    </template>
    <div v-if="isLoading"
         class="display-flex flex-row justify-between gap-16"
    >
      <slot v-for="(_, index) in Array.from({ length: panelsPerView })"
            :key="index"
            :class-name="'align-self-stretch'"
            name="loader"
      ></slot>
    </div>
    <Flicking v-else-if="data && data.length && !isLoading && !isError"
              ref="flickingInstance"
              :hideBeforeInit="false"
              @ready="onReady"
              :options="{
                circular: true,
                adaptive: true,
                duration: 250,
                panelsPerView,
                align:'prev',
              }"
              :firstPanelSize="'200px'"
    >
      <slot class="slide"
            v-for="(item, index) in data"
            name="card"
            :item="item"
            :panelsPerView="panelsPerView"
            :index="index"
      ></slot>
    </Flicking>
  </div>
</template>

<style lang="scss" scoped>

.dots-height {
  height: 42px;
}

.load {
  margin-right: 17px;
}

:deep(.flicking-viewport) {
  height: auto !important;
}

.slide {
  margin-right: 17px;
}

.slide:last-child {
  margin-right: 0;
}

.fade-enter-active {
  transition: all 0.3s ease-out;
}

.fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.8, 0.9, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
